/**
 * Resolved map generation parameters.
 *
 * The content file holds the defaults; a caller (the debug panel, the map tool, a
 * future difficulty setting) may override any of them. Resolution happens once,
 * here, so no generator stage has to know where a value came from or apply its own
 * fallback — a pattern that otherwise leaves the same constant defaulted three
 * different ways in three different files.
 */

import { loadContent, type MapgenDefaults, type MapSize } from '../../../content/schema/index.js';

export type MapgenParams = MapgenDefaults;
export type MapgenOverrides = Partial<Omit<MapgenDefaults, 'size'>>;

export interface ResolvedMapConfig {
  size: MapSize;
  params: MapgenParams;
}

/**
 * Look up a map size and merge any overrides over the content defaults.
 * Throws — naming the valid options — rather than silently falling back, because a
 * mistyped size in a batch render is a mistake worth surfacing immediately.
 */
export function resolveMapConfig(sizeKey?: string, overrides?: MapgenOverrides): ResolvedMapConfig {
  const content = loadContent();
  const key = sizeKey ?? content.mapgen.defaults.size;
  const size = content.mapgen.sizes.find((s) => s.key === key);
  if (!size) {
    const known = content.mapgen.sizes.map((s) => s.key).join(', ');
    throw new Error(`Unknown map size "${key}". Known sizes: ${known}`);
  }
  return { size, params: { ...content.mapgen.defaults, ...overrides } };
}
